import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CdpDiagnostic } from "@/components/CdpDiagnostic";
import {
  Phone, PhoneCall, UserCheck, CalendarCheck, ChevronLeft, ChevronRight,
  TrendingUp, TrendingDown, Minus, Sparkles, Loader2, ArrowRight, Flame,
  Target, Wallet, Info, MapPin, ClipboardList, RefreshCw, Lightbulb, Brain,
} from "lucide-react";

/* ---------------------------------------------------------------- constantes */
const ORANGE = "var(--shariti-orange)";
const GREEN = "#1F9D55";
const BLUE = "#2F6FED";
const DANGER = "#C0392B";
const card = "rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]";
const HOUR_LABEL = (h) => `${h}h`;

const PERIODS = [
  { key: "day", label: "Jour" },
  { key: "week", label: "Semaine" },
  { key: "month", label: "Mois" },
];

/* helpers date (composantes locales, comme le reste de la plateforme) */
function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function shiftDate(iso, period, dir) {
  const d = new Date((iso || toISO(new Date())) + "T00:00:00");
  if (period === "day") d.setDate(d.getDate() + dir);
  else if (period === "week") d.setDate(d.getDate() + dir * 7);
  else d.setMonth(d.getMonth() + dir);
  return toISO(d);
}

/* =========================================================================== */
export function CockpitTelephonie() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("month");
  const [refDate, setRefDate] = useState(toISO(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/calls/cockpit", { params: { period, date: refDate } });
      setData(d);
    } catch {
      toast.error("Impossible de charger le cockpit.");
    } finally { setLoading(false); }
  }, [period, refDate]);
  useEffect(() => { load(); }, [load]);

  const mode = data?.data_mode;
  const linkSuffix = mode === "demo" ? "&data=demo" : "&data=real";
  const drill = (params) => navigate(`/appels?${params}${linkSuffix}`);

  const reseed = async () => {
    setSeeding(true);
    try {
      await api.post("/calls/demo/seed", null, { params: { force: true } });
      toast.success("Données de démonstration régénérées");
      await load();
    } catch { toast.error("Erreur lors de la génération des données de démo."); }
    finally { setSeeding(false); }
  };

  return (
    <div className="space-y-5" data-testid="cockpit-telephonie">
      {/* Barre de contrôle */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white/80 p-1" data-testid="cockpit-period-toggle">
            {PERIODS.map((p) => (
              <button key={p.key} data-testid={`cockpit-period-${p.key}`} onClick={() => setPeriod(p.key)}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${period === p.key ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white/80 p-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setRefDate((d) => shiftDate(d, period, -1))} data-testid="cockpit-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="px-2 text-sm font-bold" data-testid="cockpit-period-label">{data?.period_label || "—"}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setRefDate((d) => shiftDate(d, period, 1))} data-testid="cockpit-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setRefDate(toISO(new Date()))} data-testid="cockpit-today">Aujourd'hui</Button>
        </div>
        <div className="flex items-center gap-2">
          {mode === "demo" && <DemoBadge />}
          <Button variant="outline" size="sm" onClick={load} data-testid="cockpit-refresh"><RefreshCw className="mr-1.5 h-4 w-4" />Actualiser</Button>
        </div>
      </div>

      {mode === "demo" && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[rgba(240,190,20,0.6)] bg-[rgba(252,219,83,0.22)] px-4 py-2.5 text-[13px] text-[#5A4200]" data-testid="cockpit-demo-banner">
          <span className="flex items-start gap-2"><Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Cockpit alimenté par des <span className="font-bold">données de démonstration</span>. Il basculera automatiquement sur les données réelles dès les premiers appels.</span>
          </span>
          <Button variant="ghost" size="sm" onClick={reseed} disabled={seeding} className="h-8 text-[#5A4200] hover:bg-[rgba(240,190,20,0.25)]" data-testid="cockpit-reseed">
            {seeding ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}Régénérer la démo
          </Button>
        </div>
      )}

      {loading || !data ? <CockpitSkeleton /> : (
        <>
          <TotalsRow totals={data.totals} onDrill={drill} />
          <GaugesGrid gauges={data.gauges} period={period} refDate={refDate} onDrill={drill} navigate={navigate} mode={mode} />
          <div className="grid gap-5 @5xl/page:grid-cols-2">
            <FunnelCard funnel={data.funnel} funnelsByCdp={data.funnels_by_cdp} onDrill={drill} />
            <ParetoCard pareto={data.pareto} onDrill={drill} />
          </div>
          <HeatmapCard heatmap={data.heatmap} onDrill={drill} />
          <AiAxesCard gauges={data.gauges} period={period} refDate={refDate} mode={mode} />
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- DÉMO */
function DemoBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(252,219,83,0.5)] px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wide text-[#5A4200]" data-testid="cockpit-demo-tag">
      Démo
    </span>
  );
}

/* --------------------------------------------------------------- SKELETON */
function CockpitSkeleton() {
  return (
    <div className="space-y-5" data-testid="cockpit-loading">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-44 rounded-2xl" />)}</div>
      <div className="grid gap-5 @5xl/page:grid-cols-2"><Skeleton className="h-72 rounded-2xl" /><Skeleton className="h-72 rounded-2xl" /></div>
      <Skeleton className="h-80 rounded-2xl" />
    </div>
  );
}

/* ---------------------------------------------------------------- TOTALS */
function TotalsRow({ totals, onDrill }) {
  const items = [
    { icon: Phone, label: "Appels", value: totals.calls, color: ORANGE, drill: "" },
    { icon: PhoneCall, label: "Aboutis", value: totals.reached, color: BLUE, drill: "status=completed" },
    { icon: UserCheck, label: "Décideurs joints", value: totals.decideurs, color: "#7C3AED", drill: "decideur=oui" },
    { icon: CalendarCheck, label: "Spots décrochés", value: totals.spots, color: GREEN, drill: "outcome=accord" },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="cockpit-totals">
      {items.map((it) => (
        <button key={it.label} onClick={() => onDrill(it.drill)} data-testid={`cockpit-total-${it.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
          className={`${card} card-hover flex items-center gap-3 p-3 text-left transition-shadow`}>
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl" style={{ background: `${it.color}18`, color: it.color }}><it.icon className="h-5 w-5" /></span>
          <div>
            <div className="text-2xl font-extrabold tabular-nums leading-none">{it.value}</div>
            <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-ink">{it.label}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------- GAUGES */
function GaugesGrid({ gauges, period, refDate, onDrill, mode }) {
  if (!gauges?.length) return null;
  const dayParam = period === "day" ? `&day=${refDate}` : "";
  return (
    <section data-testid="cockpit-gauges">
      <SectionTitle icon={Target} title="Objectifs par CDP" hint="Appels & spots décrochés vs objectif (proportionnel aux heures)" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {gauges.map((g) => (
          <button key={g.cdp_id} onClick={() => onDrill(`cdp_id=${g.cdp_id}${dayParam}`.replace(/^&/, ""))} data-testid={`cockpit-gauge-${g.cdp_id}`}
            className={`${card} card-hover p-4 text-left transition-shadow`}>
            <div className="mb-3 flex items-center gap-2">
              <span className="h-3 w-3 shrink-0 rounded-full border border-black/10" style={{ background: g.color || ORANGE }} />
              <span className="break-words text-sm font-extrabold">{g.name}</span>
              <span className="ml-auto text-[10px] font-semibold text-muted-ink">{g.hours_per_day || "—"} h/j</span>
            </div>
            <div className="flex items-center justify-around">
              <RadialGauge value={g.calls} target={g.calls_target} pct={g.calls_pct} hit={g.calls_hit} label="Appels" />
              <RadialGauge value={g.spots} target={g.spots_target} pct={g.spots_pct} hit={g.spots_hit} label="Spots" small />
            </div>
            <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] font-semibold text-muted-ink">
              <span className="inline-flex items-center gap-1"><PhoneCall className="h-3 w-3" />{g.reached} aboutis</span>
              <span className="opacity-40">·</span>
              <span className="inline-flex items-center gap-1"><UserCheck className="h-3 w-3" />{g.decideurs} décideurs</span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function RadialGauge({ value, target, pct, hit, label, small }) {
  const size = small ? 78 : 96;
  const stroke = small ? 8 : 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, pct || 0));
  const dash = (p / 100) * c;
  const color = hit ? GREEN : ORANGE;
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-2)" strokeWidth={stroke} />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
            strokeLinecap="round" strokeDasharray={`${dash} ${c}`} style={{ transition: "stroke-dasharray 500ms var(--ease-out)" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`font-extrabold tabular-nums leading-none ${small ? "text-base" : "text-xl"}`}>{value}</span>
          <span className="text-[9px] text-muted-ink">/ {target}</span>
        </div>
      </div>
      <div className="mt-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide" style={{ color: hit ? GREEN : "var(--muted-ink, rgba(24,24,24,0.56))" }}>
        {label} {hit && <span className="text-[9px]">✓</span>}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- FUNNEL */
function FunnelCard({ funnel, funnelsByCdp = [], onDrill }) {
  const [sel, setSel] = useState("global");
  // Repli sur global si le CDP sélectionné n'a plus d'appels sur la période
  const found = funnelsByCdp.find((f) => f.cdp_id === sel);
  const active = sel === "global" ? funnel : (found?.funnel || funnel);
  const cdpParam = sel === "global" || !found ? "" : `&cdp_id=${sel}`;
  const stages = active?.stages || [];
  const max = stages[0]?.count || 1;
  const STAGE_META = {
    appels: { color: ORANGE, drill: "data=x" },
    aboutis: { color: BLUE, drill: "status=completed" },
    decideur: { color: "#7C3AED", drill: "decideur=oui" },
    date: { color: GREEN, drill: "outcome=accord" },
  };
  return (
    <div className={`${card} p-5`} data-testid="cockpit-funnel">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <SectionTitle icon={ClipboardList} title="Entonnoir de conversion" hint="Du 1er appel à la date obtenue" inline />
        {funnelsByCdp.length > 0 && (
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger className="h-8 w-[148px] text-xs" data-testid="funnel-cdp-select">
              <SelectValue placeholder="Global" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="global" data-testid="funnel-cdp-global">Global (tous)</SelectItem>
              {funnelsByCdp.map((f) => (
                <SelectItem key={f.cdp_id} value={f.cdp_id} data-testid={`funnel-cdp-opt-${f.cdp_id}`}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="mt-4 space-y-2.5">
        {stages.map((s, i) => {
          const meta = STAGE_META[s.key] || { color: ORANGE, drill: "data=x" };
          const w = Math.max(8, Math.round((s.count / max) * 100));
          return (
            <div key={s.key}>
              {i > 0 && (
                <div className="mb-1 flex items-center justify-center">
                  <span className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[10px] font-bold text-muted-ink" data-testid={`funnel-conv-${s.key}`}>
                    <ArrowRight className="h-3 w-3 rotate-90" />{s.from_prev}% de conversion
                  </span>
                </div>
              )}
              <button onClick={() => onDrill(`${meta.drill}${cdpParam}`)} data-testid={`funnel-stage-${s.key}`}
                className="group flex w-full items-center gap-3 rounded-xl border border-[var(--border-soft)] p-2.5 text-left transition-[border-color,box-shadow] duration-200 hover:border-[rgba(238,114,46,0.35)] hover:shadow-[var(--shadow-sm)]">
                <div className="relative h-9 flex-1 overflow-hidden rounded-lg bg-[var(--surface-2)]">
                  <div className="absolute inset-y-0 left-0 rounded-lg transition-[width] duration-500" style={{ width: `${w}%`, background: meta.color, opacity: 0.85 }} />
                  <div className="absolute inset-0 flex items-center justify-between px-3">
                    <span className="text-xs font-bold text-[var(--shariti-ink)]">{s.label}</span>
                    <span className="text-xs font-extrabold tabular-nums text-[var(--shariti-ink)]">{s.count}</span>
                  </div>
                </div>
                <span className="w-12 shrink-0 text-right text-sm font-extrabold tabular-nums" style={{ color: meta.color }}>{s.rate}%</span>
              </button>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-[rgba(31,157,85,0.08)] py-2 text-sm font-bold text-[#0F5A2F]">
        <Flame className="h-4 w-4" />Taux de transformation {sel === "global" ? "global" : `— ${found?.name || ""}`} : {active?.conversion_global || 0}%
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- PARETO */
const TREND = {
  up: { icon: TrendingUp, color: DANGER, label: "en hausse" },
  down: { icon: TrendingDown, color: GREEN, label: "en baisse" },
  flat: { icon: Minus, color: "rgba(24,24,24,0.4)", label: "stable" },
  new: { icon: TrendingUp, color: "#B4531B", label: "nouveau" },
};
function ParetoCard({ pareto, onDrill }) {
  const items = pareto?.items || [];
  const max = items[0]?.count || 1;
  return (
    <div className={`${card} p-5`} data-testid="cockpit-pareto">
      <SectionTitle icon={TrendingDown} title="Causes de refus (Pareto)" hint={`${pareto?.total_refusals || 0} refus sur la période`} inline />
      {items.length === 0 ? (
        <EmptyMini text="Aucun refus enregistré sur la période." />
      ) : (
        <div className="mt-4 space-y-2">
          {items.slice(0, 8).map((it) => {
            const t = TREND[it.trend];
            const w = Math.max(6, Math.round((it.count / max) * 100));
            return (
              <button key={it.cause} onClick={() => onDrill(`cause=${encodeURIComponent(it.cause)}`)} data-testid={`pareto-bar-${it.cause}`}
                className="group w-full text-left">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="break-words pr-2 font-semibold text-[var(--shariti-ink)]">{it.cause}</span>
                  <span className="flex shrink-0 items-center gap-1.5 text-muted-ink">
                    <span className="tabular-nums font-bold text-[var(--shariti-ink)]">{it.count}</span>
                    <span>({it.pct}%)</span>
                    {t ? (
                      <t.icon className="h-3 w-3" style={{ color: t.color }} data-testid={`pareto-trend-${it.cause}`} />
                    ) : (
                      <span title="Volume insuffisant pour une tendance fiable"
                        className="text-[9px] font-semibold text-muted-ink opacity-60" data-testid={`pareto-trend-na-${it.cause}`}>n.s.</span>
                    )}
                  </span>
                </div>
                <div className="mt-0.5 h-3 overflow-hidden rounded-full bg-[var(--surface-2)]">
                  <div className="h-full rounded-full transition-[width,filter] duration-500 group-hover:brightness-95"
                    style={{ width: `${w}%`, background: DANGER, opacity: 0.8 }} />
                </div>
              </button>
            );
          })}
          <div className="pt-1 text-[10px] text-muted-ink">Cliquez sur une cause pour ouvrir les appels concernés. « n.s. » = tendance non significative (volume trop faible).</div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- HEATMAP */
function heatColor(rate, calls) {
  if (!calls) return "var(--surface-2)";
  const a = 0.14 + (rate / 100) * 0.72;
  return `rgba(31,157,85,${a.toFixed(2)})`;
}
// Motif hachuré pour les cellules à faible échantillon (fiabilité limitée)
const HATCH = "repeating-linear-gradient(45deg, rgba(24,24,24,0.16) 0, rgba(24,24,24,0.16) 1.5px, transparent 1.5px, transparent 5px)";
function cellStyle(cell) {
  if (!cell.calls) return { background: "var(--surface-2)" };
  if (cell.low_sample) {
    return { backgroundColor: "rgba(31,157,85,0.10)", backgroundImage: HATCH };
  }
  return { background: heatColor(cell.rate, cell.calls) };
}
function HeatmapCard({ heatmap, onDrill }) {
  const { days = [], day_labels = [], hours = [], cells = [], recommendations = [], min_sample = 5 } = heatmap || {};
  const cellMap = useMemo(() => {
    const m = {};
    for (const c of cells) m[`${c.day}-${c.hour}`] = c;
    return m;
  }, [cells]);

  return (
    <div className={`${card} p-5`} data-testid="cockpit-heatmap">
      <SectionTitle icon={MapPin} title="Joignabilité des décideurs" hint="Taux de décideur joint par jour et créneau horaire" inline />
      {recommendations?.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-[rgba(31,157,85,0.08)] px-3 py-2 text-[12px] text-[#0F5A2F]" data-testid="heatmap-reco">
          <Lightbulb className="h-4 w-4 shrink-0" />
          <span className="font-bold">Meilleurs créneaux :</span>
          {recommendations.map((r, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold shadow-[var(--shadow-sm)]" data-testid={`heatmap-reco-${i}`}>
              {r.day_label} {r.hour}h · {r.rate}% <span className="font-semibold text-muted-ink">({r.calls} app.)</span>
            </span>
          ))}
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-[var(--surface-2)] px-3 py-2 text-[12px] text-muted-ink" data-testid="heatmap-reco-empty">
          <Info className="h-4 w-4 shrink-0" />
          <span>Pas encore assez d'appels par créneau pour recommander un horaire fiable (seuil&nbsp;: {min_sample} appels).</span>
        </div>
      )}
      {/* overflow-x-auto : le tableau seul défile si l'écran est étroit (jamais la page) */}
      <div className="mt-4 overflow-x-auto">
        <div className="min-w-[560px]">
          <div className="flex items-center gap-1">
            <div className="w-10 shrink-0" />
            {hours.map((h) => (
              <div key={h} className="flex-1 text-center text-[10px] font-semibold text-muted-ink">{HOUR_LABEL(h)}</div>
            ))}
          </div>
          {days.map((d, di) => (
            <div key={d} className="mt-1 flex items-center gap-1">
              <div className="w-10 shrink-0 text-[11px] font-bold text-muted-ink">{day_labels[di]}</div>
              {hours.map((h) => {
                const cell = cellMap[`${d}-${h}`] || { calls: 0, rate: 0, decideurs: 0 };
                const disabled = cell.calls === 0;
                const tip = disabled
                  ? `${day_labels[di]} ${h}h — aucun appel`
                  : `${day_labels[di]} ${h}h — ${cell.decideurs} décideur(s) joint(s) / ${cell.calls} appel(s) (${cell.rate}%)`
                    + (cell.low_sample ? ` · échantillon insuffisant (< ${min_sample} appels, fiabilité limitée)` : "");
                const txtColor = disabled ? "text-transparent"
                  : cell.low_sample ? "text-[rgba(24,24,24,0.5)] italic" : "text-[#0F5A2F]";
                return (
                  <button key={h} disabled={disabled} onClick={() => onDrill(`weekday=${d}&hour=${h}`)}
                    data-testid={`heat-${d}-${h}`} title={tip}
                    className={`aspect-square flex-1 rounded-md text-[9px] font-bold tabular-nums transition-transform ${disabled ? "cursor-default" : "cursor-pointer hover:scale-[1.08]"} ${txtColor}`}
                    style={cellStyle(cell)}>
                    {cell.calls ? `${cell.rate}` : ""}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-end gap-x-3 gap-y-1.5 text-[10px] text-muted-ink">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-6 rounded" style={{ backgroundColor: "rgba(31,157,85,0.10)", backgroundImage: HATCH }} />
          Échantillon insuffisant (&lt; {min_sample} app.)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span>Faible</span>
          <span className="h-3 w-6 rounded" style={{ background: "rgba(31,157,85,0.16)" }} />
          <span className="h-3 w-6 rounded" style={{ background: "rgba(31,157,85,0.5)" }} />
          <span className="h-3 w-6 rounded" style={{ background: "rgba(31,157,85,0.86)" }} />
          <span>Élevé</span>
        </span>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- AXES IA */
function AiAxesCard({ gauges, period, refDate, mode }) {
  const [cdpId, setCdpId] = useState(null);
  const [res, setRes] = useState({});           // {cdpId: {axes, generated_at}}
  const [loading, setLoading] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);

  useEffect(() => {
    if (!cdpId && gauges?.length) setCdpId(gauges[0].cdp_id);
  }, [gauges, cdpId]);

  const current = res[cdpId];

  const generate = async (force = false) => {
    if (!cdpId) return;
    setLoading(true);
    try {
      const { data } = await api.post("/calls/cockpit/ai-axes", { cdp_id: cdpId, period, date: refDate, force });
      setRes((p) => ({ ...p, [cdpId]: data }));
    } catch { toast.error("Impossible de générer les axes IA."); }
    finally { setLoading(false); }
  };

  const cdpName = (gauges || []).find((g) => g.cdp_id === cdpId)?.name;

  return (
    <div className={`${card} p-5`} data-testid="cockpit-ai-axes">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle icon={Sparkles} title="Axes d'amélioration IA" hint="Conseils constructifs, générés par l'IA à partir des appels" inline />
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1" data-testid="ai-axes-cdp-select">
            {(gauges || []).map((g) => (
              <button key={g.cdp_id} onClick={() => setCdpId(g.cdp_id)} data-testid={`ai-axes-cdp-${g.cdp_id}`}
                className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors ${cdpId === g.cdp_id ? "bg-[var(--shariti-orange)] text-white" : "bg-[var(--surface-2)] text-muted-ink hover:bg-[rgba(238,114,46,0.12)]"}`}>
                {g.name}
              </button>
            ))}
          </div>
          <Button size="sm" onClick={() => generate(!!current)} disabled={loading || !cdpId}
            className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="ai-axes-generate">
            {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
            {current ? "Régénérer" : "Générer les axes"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setDiagOpen(true)} disabled={!cdpId}
            className="border-[rgba(238,114,46,0.5)] text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.08)] hover:text-[var(--shariti-orange)]"
            data-testid="ai-axes-diagnostic">
            <Brain className="mr-1.5 h-4 w-4" />Diagnostic complet
          </Button>
        </div>
      </div>

      <CdpDiagnostic
        cdpId={cdpId}
        cdpName={cdpName}
        open={diagOpen}
        onOpenChange={setDiagOpen}
        initialPeriod={period}
        initialDate={refDate}
      />

      {!current ? (
        <div className="mt-4 flex flex-col items-center justify-center gap-2 rounded-xl bg-[var(--surface-2)] py-10 text-center" data-testid="ai-axes-empty">
          <Lightbulb className="h-7 w-7 text-[var(--shariti-orange)]" />
          <p className="text-sm font-semibold">Axes d'amélioration pour {cdpName || "ce CDP"}</p>
          <p className="max-w-md text-xs text-muted-ink">Cliquez sur « Générer les axes » pour obtenir 3 conseils concrets et bienveillants, basés sur les statistiques d'appels de la période.</p>
        </div>
      ) : (
        <div className="mt-4 grid gap-3 @3xl/page:grid-cols-3" data-testid="ai-axes-list">
          {(current.axes || []).map((a, i) => (
            <div key={i} className="rounded-xl border border-[var(--border-soft)] bg-[rgba(238,114,46,0.03)] p-4" data-testid={`ai-axis-${i}`}>
              <div className="flex items-center gap-2">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-[var(--shariti-orange)] text-[11px] font-extrabold text-white">{i + 1}</span>
                <h4 className="text-sm font-extrabold leading-tight">{a.title}</h4>
              </div>
              {a.constat && <p className="mt-2 text-xs text-muted-ink">{a.constat}</p>}
              {a.action && (
                <p className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-[#0F5A2F]">
                  <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0" />{a.action}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- helpers UI */
function SectionTitle({ icon: Icon, title, hint, inline }) {
  return (
    <div className={inline ? "" : "mb-3"}>
      <h3 className="flex items-center gap-2 text-sm font-bold">
        <span className="grid h-7 w-7 place-items-center rounded-lg bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]"><Icon className="h-4 w-4" /></span>
        {title}
      </h3>
      {hint && <p className="mt-1 pl-9 text-[11px] text-muted-ink">{hint}</p>}
    </div>
  );
}
function EmptyMini({ text }) {
  return <div className="mt-4 rounded-xl bg-[var(--surface-2)] px-4 py-8 text-center text-xs text-muted-ink" data-testid="cockpit-empty-mini">{text}</div>;
}
